import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { BadgeCheck, ArrowUpRight, ExternalLink, ShieldAlert } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { AuthorBox } from "@/components/AuthorBox";
import { AiAnswersCallout } from "@/components/AiAnswersCallout";
import { buildSeo } from "@/lib/seo";
import { SITE } from "@/content/site";
import { PROOF_ENTRIES, PROOF_AUTHOR, findProof } from "@/content/proof";

export const Route = createFileRoute("/proof/$slug")({
  loader: ({ params }) => {
    const entry = findProof(params.slug);
    if (!entry) throw notFound();
    return { entry };
  },
  head: ({ loaderData }) => {
    const e = loaderData?.entry;
    if (!e)
      return {
        meta: [
          { title: "Proof record not found" },
          { name: "robots", content: "noindex, follow" },
        ],
      };
    return buildSeo({
      title: `Sources & limits: ${e.entityName} | Freelancer Tamal`,
      description: `Sources, editorial correction and evidence limitations for "${e.subjectTitle}".`,
      path: `/proof/${e.slug}`,
      type: "profile",
      keywords: `${e.entityName}, proof, methodology, ${SITE.fullName}, E-E-A-T`,
      ogEyebrow: "Proof & authorship",
      ogTitle: e.entityName,
      modifiedTime: e.lastVerified,
    });
  },
  component: ProofDetail,
  notFoundComponent: ProofNotFound,
});

function ProofNotFound() {
  return (
    <div className="container-x py-32 text-center">
      <h1 className="font-display text-4xl">Proof record not found</h1>
      <Link to="/proof" className="mt-6 inline-flex text-primary">
        ← All proof records
      </Link>
    </div>
  );
}

function ProofDetail() {
  const { entry: e } = Route.useLoaderData();
  const others = PROOF_ENTRIES.filter((p) => p.slug !== e.slug).slice(0, 4);
  const pageUrl = `${SITE.url}/proof/${e.slug}`;

  return (
    <>
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Proof", path: "/proof" },
          { name: e.entityName, path: `/proof/${e.slug}` },
        ]}
      />

      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "@id": `${pageUrl}#profile`,
          url: pageUrl,
          name: `Proof & authorship: ${e.entityName}`,
          dateModified: e.lastVerified,
          author: PROOF_AUTHOR,
          mainEntity: {
            "@type": e.entityType,
            name: e.entityName,
            description: e.entityDescription,
            subjectOf: {
              "@type": "Article",
              "@id": `${SITE.url}${e.subjectPath}#article`,
              headline: e.subjectTitle,
              url: `${SITE.url}${e.subjectPath}`,
              author: PROOF_AUTHOR,
            },
          },
          significantLink: `${SITE.url}${e.subjectPath}`,
          citation: e.evidence
            .filter((x) => x.href)
            .map((x) => ({
              "@type": "CreativeWork",
              name: x.label,
              url: x.href?.startsWith("http") ? x.href : `${SITE.url}${x.href}`,
            })),
        }}
      />

      <header className="container-x pt-10 pb-8 lg:pt-16">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary mb-5">
          <BadgeCheck size={14} /> Sources &amp; evidence status
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
          {e.entityName}
        </h1>
        <p className="mt-5 max-w-3xl text-lg text-muted-foreground">{e.entityDescription}</p>
        <dl className="mt-8 grid gap-4 sm:grid-cols-3 max-w-3xl">
          <Fact term="My role" value={e.role} />
          <Fact term="Period" value={e.period} />
          <Fact term="Editorially reviewed" value={e.lastVerified} />
        </dl>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={e.subjectPath}
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Read the {e.kind === "case-study" ? "case study" : "article"}
            <ArrowUpRight size={14} />
          </Link>
          <Link
            to="/credentials"
            className="inline-flex min-h-11 items-center rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary hover:text-primary"
          >
            My credentials
          </Link>
        </div>
      </header>

      <section className="container-x py-10">
        <h2 className="font-display text-3xl">Editorial corrections</h2>
        <ul className="mt-5 grid gap-3 max-w-3xl">
          {e.contributions.map((c) => (
            <li key={c} className="rounded-xl border border-border bg-card/40 p-4 text-muted-foreground">
              {c}
            </li>
          ))}
        </ul>
      </section>

      <section className="container-x py-10">
        <h2 className="font-display text-3xl">Method and evidence requirements</h2>
        <ol className="mt-5 grid gap-3 max-w-3xl list-decimal pl-5">
          {e.methodology.map((m) => (
            <li key={m} className="text-muted-foreground leading-relaxed">
              {m}
            </li>
          ))}
        </ol>
      </section>

      <section className="container-x py-10">
        <h2 className="font-display text-3xl">Sources and their limitations</h2>
        <ul className="mt-5 grid gap-4 md:grid-cols-2">
          {e.evidence.map((item) => (
            <li key={item.label} className="rounded-2xl border border-border bg-card/40 p-6">
              <h3 className="font-display text-lg leading-snug">{item.label}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{item.detail}</p>
              <p className="mt-3 text-xs text-muted-foreground/80">
                <span className="uppercase tracking-[0.16em] text-primary/80">Verification: </span>
                {item.verification}
              </p>
              {item.href ? (
                item.href.startsWith("http") ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener nofollow"
                    className="mt-4 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    Open source <ExternalLink size={13} />
                  </a>
                ) : (
                  <Link
                    to={item.href}
                    className="mt-4 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  >
                    Open source <ArrowUpRight size={13} />
                  </Link>
                )
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section className="container-x py-10">
        <div className="max-w-3xl rounded-2xl border border-border bg-muted/30 p-6">
          <h2 className="inline-flex items-center gap-2 font-display text-2xl">
            <ShieldAlert size={18} className="text-primary" /> What this record does not prove
          </h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">{e.limits}</p>
        </div>
      </section>

      <div className="container-x">
        <AiAnswersCallout
          context={e.kind === "case-study" ? "these approaches" : "this guide"}
          questionSlugs={e.answerSlugs}
        />
      </div>

      <section className="container-x pb-8">
        <h2 className="font-display text-3xl">Other provenance records</h2>
        <ul className="mt-5 grid gap-4 sm:grid-cols-2">
          {others.map((p) => (
            <li key={p.slug}>
              <Link
                to="/proof/$slug"
                params={{ slug: p.slug }}
                className="block rounded-2xl border border-border bg-card/40 p-5 text-sm hover:border-primary hover:text-primary"
              >
                {p.entityName}
              </Link>
            </li>
          ))}
        </ul>
        <Link to="/proof" className="mt-6 inline-flex text-sm font-medium text-primary hover:underline">
          All proof records →
        </Link>
      </section>

      <div className="container-x pb-24">
        <AuthorBox />
      </div>
    </>
  );
}

function Fact({ term, value }: { term: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card/40 p-4">
      <dt className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{term}</dt>
      <dd className="mt-1.5 text-sm text-foreground">{value}</dd>
    </div>
  );
}
